/**
 * Phonetic System Converter (Frontend)
 * Converts between IPA, KK (Taiwan), DJ (China/UK), and Jones (Japan) phonetic systems
 */

import { PhoneticSystem } from '../types/auth';

/**
 * IPA to KK mapping (main differences)
 * KK uses simplified notation for diphthongs
 */
const IPA_TO_KK: [RegExp, string][] = [
    // Diphthongs - KK uses single symbols
    [/eɪ/g, 'e'],      // date, make
    [/oʊ/g, 'o'],      // hope, go
    [/əʊ/g, 'o'],      // British "go" variant

    // R-colored vowels
    [/ɜːr/g, 'ɝ'],     // bird
    [/ɜɹ/g, 'ɝ'],      // bird variant
    [/əɹ/g, 'ɚ'],      // butter
    [/ər/g, 'ɚ'],      // butter variant

    // Consonants
    [/ɹ/g, 'r'],       // red

    // Long vowel markers (remove length mark)
    [/iː/g, 'i'],      // see
    [/uː/g, 'u'],      // too
    [/ɔː/g, 'ɔ'],      // law
    [/ɑː/g, 'ɑ'],      // father
];

/**
 * IPA to Jones (Japan) mapping
 * Jones simplifies vowel distinctions
 */
const IPA_TO_JONES: [RegExp, string][] = [
    // Merge short/long vowel pairs
    [/ɪ/g, 'i'],       // bit → i (same as beat)
    [/ʊ/g, 'u'],       // put → u (same as boot)

    // Diphthongs - Jones often uses single symbols
    [/eɪ/g, 'e'],      // date
    [/oʊ/g, 'o'],      // hope
    [/əʊ/g, 'o'],      // British go

    // R-colored vowels (simplified)
    [/ɜːr/g, 'əːr'],
    [/ər/g, 'ər'],

    // Remove length marks (Jones doesn't distinguish length as strictly)
    [/ː/g, ''],
];

/**
 * Apply conversion rules to phonetic string
 */
function applyConversions(phonetic: string, rules: [RegExp, string][]): string {
    let result = phonetic;
    for (const [pattern, replacement] of rules) {
        result = result.replace(pattern, replacement);
    }
    return result;
}

/**
 * Convert phonetic notation from IPA to target system
 *
 * @param phonetic - The phonetic string to convert (assumed to be IPA/ECDICT format)
 * @param to - Target phonetic system
 * @returns Converted phonetic string
 */
export function convertPhonetic(
    phonetic: string | undefined | null,
    to: PhoneticSystem = 'ipa'
): string {
    if (!phonetic) {
        return '';
    }

    // ECDICT uses a format similar to IPA but with some variations
    // Normalize first if needed
    const normalized = phonetic;

    switch (to) {
        case 'ipa':
            return normalized;

        case 'kk':
            return applyConversions(normalized, IPA_TO_KK);

        case 'dj':
            // DJ is very close to IPA, mainly differs in stress notation
            return normalized;

        case 'jones':
            return applyConversions(normalized, IPA_TO_JONES);

        default:
            return phonetic;
    }
}

/**
 * Get user's phonetic system preference from localStorage
 */
export function getUserPhoneticSystem(): PhoneticSystem {
    const validSystems: PhoneticSystem[] = ['ipa', 'kk', 'dj', 'jones'];
    try {
        const userStr = localStorage.getItem('user');
        if (userStr) {
            const user = JSON.parse(userStr);
            if (user.phoneticSystem && validSystems.includes(user.phoneticSystem)) {
                return user.phoneticSystem;
            }
        }
    } catch (e) {
        console.warn('Failed to parse user phonetic preference from localStorage:', e);
    }
    // Default to KK for Taiwan users
    return 'kk';
}

/**
 * Get human-readable name for phonetic system
 */
export function getPhoneticSystemName(system: PhoneticSystem): string {
    const names: Record<PhoneticSystem, string> = {
        'ipa': '國際音標 (IPA)',
        'kk': 'KK音標 (美式)',
        'dj': 'DJ音標 (英式)',
        'jones': 'Jones式音標',
    };
    return names[system] || names['ipa'];
}
